import React, { useState, useEffect } from 'react';
import { machinesAPI } from '../services/api';
import { PaginationControls } from '../components/PaginationControls';
import { useClientPagination } from '../hooks/useClientPagination';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Cog, Pencil, Trash2 } from 'lucide-react';

const PROCESS_TYPES = [
  'Cold Forging',
  'Hot Forging',
  'Thread Rolling',
  'Heat Treatment',
  'Zinc Plating',
  'Phosphating',
  'CNC Machining',
  'Centerless Grinding',
  'Wire Drawing',
  'Trimming',
  'Pointing',
  'Annealing',
  'Shot Blasting',
  'Inspection',
  'Packing',
  'Other'
];

export default function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processTypeFilter, setProcessTypeFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const { isAdmin } = useAuth();
  const pagination = useClientPagination(machines);

  useEffect(() => {
    fetchMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processTypeFilter]);

  const fetchMachines = async () => {
    try {
      const params = { is_active: true };
      if (processTypeFilter) params.process_type = processTypeFilter;
      const response = await machinesAPI.list(params);
      setMachines(response.data);
    } catch (error) {
      toast.error('Failed to fetch machines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (data) => {
    try {
      if (editingMachine) {
        await machinesAPI.update(editingMachine.id, data);
        toast.success('Machine updated successfully');
      } else {
        await machinesAPI.create(data);
        toast.success('Machine created successfully');
      }
      setDialogOpen(false);
      setEditingMachine(null);
      fetchMachines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (machine) => {
    if (!window.confirm(`Are you sure you want to deactivate ${machine.name}?`)) return;
    
    try {
      await machinesAPI.delete(machine.id);
      toast.success('Machine deactivated');
      fetchMachines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const openEditDialog = (machine) => {
    setEditingMachine(machine);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            Machine Master
          </h1>
          <p className="text-muted-foreground">Manage manufacturing machines and rates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingMachine(null);
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-machine-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Machine
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingMachine ? 'Edit Machine' : 'Add New Machine'}
              </DialogTitle>
            </DialogHeader>
            <MachineForm
              machine={editingMachine}
              onSubmit={handleCreateOrUpdate}
              onCancel={() => {
                setDialogOpen(false);
                setEditingMachine(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex items-center gap-4">
            <Select value={processTypeFilter || 'all'} onValueChange={(v) => setProcessTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]" data-testid="process-type-filter">
                <SelectValue placeholder="All Process Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Process Types</SelectItem>
                {PROCESS_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : machines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Cog className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No machines found</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Machine Name</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Process Type</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Strokes/min</TableHead>
                    <TableHead>Hourly Rate</TableHead>
                    <TableHead>OEE %</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageRows.map((machine) => (
                    <TableRow key={machine.id} className="table-row">
                      <TableCell className="font-medium">{machine.name}</TableCell>
                      <TableCell>{machine.model || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{machine.process_type}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">
                        {machine.capacity_tons ? `${machine.capacity_tons}T` : '-'}
                      </TableCell>
                      <TableCell className="font-mono">
                        {machine.strokes_per_minute || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-primary">
                        ₹{machine.hourly_rate.toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono">{machine.oee_percentage}%</TableCell>
                      <TableCell>
                        <Badge className={machine.is_active ? 'badge-success' : 'badge-error'}>
                          {machine.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(machine)}
                            data-testid={`edit-machine-${machine.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(machine)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-machine-${machine.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <PaginationControls
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onLimitChange={pagination.setLimit}
            onPageChange={pagination.setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MachineForm({ machine, onSubmit, onCancel }) {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: machine?.name || '',
    model: machine?.model || '',
    manufacturer: machine?.manufacturer || '',
    process_type: machine?.process_type || '',
    capacity_tons: machine?.capacity_tons || '',
    strokes_per_minute: machine?.strokes_per_minute || '',
    hourly_rate: machine?.hourly_rate || '',
    power_consumption_kw: machine?.power_consumption_kw || '',
    operator_count: machine?.operator_count || 1,
    oee_percentage: machine?.oee_percentage || 85,
    maintenance_cost_per_hour: machine?.maintenance_cost_per_hour || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      capacity_tons: formData.capacity_tons ? parseFloat(formData.capacity_tons) : null,
      strokes_per_minute: formData.strokes_per_minute ? parseFloat(formData.strokes_per_minute) : null,
      hourly_rate: parseFloat(formData.hourly_rate),
      power_consumption_kw: formData.power_consumption_kw ? parseFloat(formData.power_consumption_kw) : null,
      operator_count: parseInt(formData.operator_count),
      oee_percentage: parseFloat(formData.oee_percentage),
      maintenance_cost_per_hour: formData.maintenance_cost_per_hour ? parseFloat(formData.maintenance_cost_per_hour) : null,
    };
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Machine Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            data-testid="machine-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="model">Model</Label>
          <Input
            id="model"
            name="model"
            value={formData.model}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="manufacturer">Manufacturer</Label>
          <Input
            id="manufacturer"
            name="manufacturer"
            value={formData.manufacturer}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="process_type">Process Type *</Label>
          <Select
            value={formData.process_type}
            onValueChange={(value) => setFormData(prev => ({ ...prev, process_type: value }))}
          >
            <SelectTrigger data-testid="machine-process-type-select">
              <SelectValue placeholder="Select process type" />
            </SelectTrigger>
            <SelectContent>
              {PROCESS_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="capacity_tons">Capacity (Tons)</Label>
          <Input
            id="capacity_tons"
            name="capacity_tons"
            type="number"
            step="0.1"
            value={formData.capacity_tons}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="strokes_per_minute">Strokes/Minute</Label>
          <Input
            id="strokes_per_minute"
            name="strokes_per_minute"
            type="number"
            value={formData.strokes_per_minute}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="oee_percentage">OEE %</Label>
          <Input
            id="oee_percentage"
            name="oee_percentage"
            type="number"
            step="0.1"
            value={formData.oee_percentage}
            onChange={handleChange}
            data-testid="machine-oee-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="hourly_rate">Hourly Rate (₹) *</Label>
          <Input
            id="hourly_rate"
            name="hourly_rate"
            type="number"
            step="0.01"
            value={formData.hourly_rate}
            onChange={handleChange}
            required
            disabled={machine && !isAdmin()}
            data-testid="machine-hourly-rate-input"
          />
          {machine && !isAdmin() && (
            <p className="text-xs text-muted-foreground">Contact admin to modify rates</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="operator_count">Operator Count</Label>
          <Input
            id="operator_count"
            name="operator_count"
            type="number"
            value={formData.operator_count}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="power_consumption_kw">Power (kW)</Label>
          <Input
            id="power_consumption_kw"
            name="power_consumption_kw"
            type="number"
            step="0.1"
            value={formData.power_consumption_kw}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="maintenance_cost_per_hour">Maintenance Cost/Hr (₹)</Label>
          <Input
            id="maintenance_cost_per_hour"
            name="maintenance_cost_per_hour"
            type="number"
            step="0.01"
            value={formData.maintenance_cost_per_hour}
            onChange={handleChange}
            disabled={machine && !isAdmin()}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="btn-primary" data-testid="save-machine-btn">
          {machine ? 'Update' : 'Create'} Machine
        </Button>
      </div>
    </form>
  );
}
